/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { useStartupState } from '../../Providers/StartupState';
import { handleGetData } from '../../Api';
import { Loading } from '../../components/Loading';
import { Capa } from '../../components/Capa';
import { Acao, Cabecalho, Foto, OdsContexto, OdsLista, irParaSecao, preencher, usePropsDaAcao } from './comum';
import { Certificacoes, Destaques } from './Destaques';
import { Emissoes } from './Emissoes';
import { Eucalipto } from './Eucalipto';
import { Documentos } from './Documentos';
import { emissoesPorAno, lerEmissoes, lerRemocoes } from './gee';
import * as S from './style';

// Os três pilares, na ordem ESG. Só a cor e a âncora são do desenho: os
// cartões do posicionamento e os grupos da materialidade usam a mesma paleta, e
// o schema pede os cartões nesta ordem. `chave` é o valor do dropdown "pilar"
// do tema-material, não texto de tela: o que aparece vem do Squidex.
const PILARES = [
  { chave: 'Ambiental', cor: '#0D6933', rgb: '13, 105, 51', id: 'materialidade-ambiental' },
  { chave: 'Social', cor: '#A64F03', rgb: '166, 79, 3', id: 'materialidade-social' },
  { chave: 'Governança', cor: '#0B4F80', rgb: '11, 79, 128', id: 'materialidade-governanca' },
];

// Ordem das seções na página e no menu "Nesta página". O nome de cada uma no
// menu vem do componente `menu`; vazio, vale o chapéu da própria seção.
// Seção vazia no Squidex some da página e daqui.
const SECOES = [
  { campo: 'posicionamento', id: 'posicionamento' },
  { campo: 'materialidade', id: 'materialidade' },
  { campo: 'destaques', id: 'destaques' },
  { campo: 'certificacoes', id: 'certificacoes' },
  { campo: 'emissoes', id: 'emissoes' },
  { campo: 'eucalipto', id: 'eucalipto' },
  { campo: 'documentos', id: 'conteudos' },
];

// Coleção inteira, página a página: o Squidex limita cada resposta, e as
// coleções de GEE crescem um ano por unidade.
const buscarTodos = async (token, schema, extra = '') => {
  const itens = [];
  for (let pular = 0; ; pular += 200) {
    const r = await handleGetData(token, `api/content/copasul/${schema}/?$top=200&$skip=${pular}${extra}`);
    const lote = r?.data?.items || [];
    itens.push(...lote);
    if (!lote.length || itens.length >= (r?.data?.total ?? 0)) return itens;
  }
};

// Uma coleção que falha não derruba a página: a seção dela só fica sem dados.
const semFalhar = (promessa, rotulo) =>
  promessa.catch((e) => {
    console.log(rotulo, e.message);
    return [];
  });

const iv = (item, campo) => item?.data?.[campo]?.iv;


const lerOds = (items) => {
  const mapa = {};
  items.forEach((item) => {
    const n = iv(item, 'numero');
    if (n) mapa[n] = { nome: iv(item, 'nome') || '', rotulo: iv(item, 'rotulo'), icone: iv(item, 'icone')?.[0] };
  });
  return mapa;
};

const Posicionamento = ({ secao }) => {
  const propsDaAcao = usePropsDaAcao();
  const cartoes = secao.cartoes || [];

  return (
    <S.Secao id="posicionamento">
      <S.Container>
        <S.Posicionamento>
          <div>
            <Cabecalho chapeu={secao.chapeu} titulo={secao.titulo} descricao={secao.descricao} markdown />
          </div>
          {cartoes.length > 0 && (
            <S.Pilares>
              {cartoes.map((c, i) => {
                const pilar = PILARES[i % PILARES.length];
                const link = propsDaAcao(c.acao);
                return (
                  <S.Pilar key={i} as={link ? 'a' : 'div'} $rgb={pilar.rgb} {...(link || {})}>
                    <Foto id={c.imagem?.[0]} largura={900} alt="" />
                    <span />
                    <div>
                      <strong>
                        {c.sigla && <span aria-hidden="true">{c.sigla}</span>}
                        {c.titulo}
                      </strong>
                      {c.descricao && <p>{c.descricao}</p>}
                      {link && c.acao?.rotulo && <em>{c.acao.rotulo}</em>}
                    </div>
                  </S.Pilar>
                );
              })}
            </S.Pilares>
          )}
        </S.Posicionamento>
      </S.Container>
    </S.Secao>
  );
};

const Materialidade = ({ secao }) => {
  const temas = secao.temas || [];
  const contagem = (n) => preencher(n === 1 ? secao.contagemUm : secao.contagemVarios, { n });

  return (
    <S.Secao id="materialidade" $wash>
      <S.Container>
        <Cabecalho chapeu={secao.chapeu} titulo={secao.titulo} descricao={secao.descricao} />
        <S.Grupos>
          {PILARES.map((pilar) => {
            const doPilar = temas.filter((t) => t.pilar === pilar.chave);
            if (!doPilar.length) return null;
            const quantos = contagem(doPilar.length);
            return (
              <S.Grupo key={pilar.chave} id={pilar.id} $cor={pilar.cor}>
                <div>
                  <h3>{doPilar[0].pilar}</h3>
                  {quantos && <p>{quantos}</p>}
                </div>
                {doPilar.map((t, i) => (
                  <S.Tema key={i} $cor={pilar.cor}>
                    <p>{t.titulo}</p>
                    <p>{t.descricao}</p>
                    <OdsLista numeros={t.ods} tamanho={52} />
                  </S.Tema>
                ))}
              </S.Grupo>
            );
          })}
        </S.Grupos>
      </S.Container>
    </S.Secao>
  );
};

// Menu fixo no topo com a seção atual sublinhada. A seção atual é a última
// cujo topo já passou da linha logo abaixo do menu.
const NavDaPagina = ({ secoes, chamada }) => {
  const [atual, setAtual] = useState(secoes[0]?.id);
  const trilho = useRef(null);

  useEffect(() => {
    let quadro = null;
    const aoRolar = () => {
      if (quadro) return;
      quadro = requestAnimationFrame(() => {
        quadro = null;
        const alvos = secoes.map((s) => document.getElementById(s.id)).filter(Boolean);
        if (!alvos.length) return;
        let id = alvos[0].id;
        alvos.forEach((el) => {
          if (el.getBoundingClientRect().top <= 120) id = el.id;
        });
        const noFim = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 8;
        if (noFim) id = alvos[alvos.length - 1].id;
        setAtual(id);
      });
    };
    window.addEventListener('scroll', aoRolar, { passive: true });
    window.addEventListener('resize', aoRolar, { passive: true });
    aoRolar();
    return () => {
      window.removeEventListener('scroll', aoRolar);
      window.removeEventListener('resize', aoRolar);
      if (quadro) cancelAnimationFrame(quadro);
    };
  }, [secoes]);

  // Leva o sublinhado até o link atual e, no celular, rola o trilho para o
  // link não ficar escondido fora da tela.
  useEffect(() => {
    const t = trilho.current;
    const link = t?.querySelector('a[aria-current="true"]');
    if (!link) return;
    t.style.setProperty('--ind-x', `${link.offsetLeft}px`);
    t.style.setProperty('--ind-w', `${link.offsetWidth}px`);
    const fora = link.offsetLeft < t.scrollLeft || link.offsetLeft + link.offsetWidth > t.scrollLeft + t.clientWidth;
    if (fora) t.scrollTo({ left: link.offsetLeft - 24, behavior: 'smooth' });
  }, [atual]);

  return (
    <S.Nav aria-label={chamada || undefined}>
      <S.Container>
        <S.NavTrilho ref={trilho}>
          {chamada && <p>{chamada}</p>}
          {secoes.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              aria-current={atual === s.id ? 'true' : 'false'}
              onClick={(e) => {
                if (irParaSecao(s.id)) e.preventDefault();
              }}
            >
              {s.rotulo}
            </a>
          ))}
        </S.NavTrilho>
      </S.Container>
    </S.Nav>
  );
};

export const Sustainability = () => {
  const [dados, setDados] = useState(null);
  const [pronto, setPronto] = useState(false);

  const { setTitle, setBgHeader } = useHeaderStates();
  const { token, setError, setLoading } = useStartupState();

  useEffect(() => {
    setError(null);
    setLoading(true);
    if (!token) return;

    Promise.all([
      handleGetData(token, 'api/content/copasul/pagina-sustentabilidade/').then((r) => r?.data?.items?.[0]?.data),
      semFalhar(buscarTodos(token, 'ods'), 'ods'),
      semFalhar(buscarTodos(token, 'gee-emissao'), 'gee-emissao'),
      semFalhar(buscarTodos(token, 'gee-remocao'), 'gee-remocao'),
    ])
      .then(([pagina, ods, emissoes, remocoes]) => {
        setDados({
          pagina: pagina || {},
          ods: lerOds(ods),
          emissoes: lerEmissoes(emissoes),
          remocoes: lerRemocoes(remocoes),
        });
      })
      .catch((e) => {
        setDados(null);
        console.log(e.message);
      })
      .finally(() => {
        setPronto(true);
        setLoading(false);
      });
  }, [token]);

  // A página tem o próprio topo: o banner do Header fica desligado, como na Home.
  useEffect(() => {
    setTitle(null);
    setBgHeader(null);
  });

  const campo = (nome) => dados?.pagina?.[nome]?.iv;
  const topo = campo('topo');
  const metadados = campo('metadados');

  // Título e descrição da aba e do Google vêm do componente `metadados`.
  useEffect(() => {
    if (!metadados) return undefined;
    const tituloAntes = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const descricaoAntes = meta?.getAttribute('content');
    if (metadados.titulo) document.title = metadados.titulo;
    if (meta && metadados.descricao) meta.setAttribute('content', metadados.descricao);
    return () => {
      document.title = tituloAntes;
      if (meta && descricaoAntes != null) meta.setAttribute('content', descricaoAntes);
    };
  }, [metadados?.titulo, metadados?.descricao]);

  const menu = campo('menu');
  const secoes = useMemo(
    () =>
      SECOES.filter((s) => dados?.pagina?.[s.campo]?.iv)
        .map((s) => ({ ...s, rotulo: menu?.[s.campo] || dados.pagina[s.campo].iv.chapeu }))
        .filter((s) => s.rotulo),
    [dados],
  );
  const serie = useMemo(() => emissoesPorAno(dados?.emissoes || []), [dados]);

  // Link direto para uma seção (/sustentabilidade#emissoes): o navegador tenta
  // rolar antes de o conteúdo existir, então a rolagem é refeita depois.
  useEffect(() => {
    if (!pronto) return;
    const id = window.location.hash.slice(1);
    if (id) requestAnimationFrame(() => irParaSecao(decodeURIComponent(id)));
  }, [pronto]);

  if (!pronto) return <Loading />;

  return (
    <OdsContexto.Provider value={dados?.ods || {}}>
      <S.Pagina>
        {topo && (
          <Capa imagem={topo.imagem?.[0]} titulo={topo.titulo} texto={topo.chamada}>
            {topo.acoes?.map((a, i) => (
              <Acao key={i} acao={a} variante="vazado" />
            ))}
          </Capa>
        )}

        {secoes.length > 1 && <NavDaPagina secoes={secoes} chamada={menu?.chamada} />}

        {campo('posicionamento') && <Posicionamento secao={campo('posicionamento')} />}
        {campo('materialidade') && <Materialidade secao={campo('materialidade')} />}
        {campo('destaques') && <Destaques secao={campo('destaques')} />}
        {campo('certificacoes') && <Certificacoes secao={campo('certificacoes')} />}
        {campo('emissoes') && <Emissoes secao={campo('emissoes')} serie={serie} />}
        {campo('eucalipto') && (
          <Eucalipto secao={campo('eucalipto')} remocoes={dados.remocoes} emissoes={dados.emissoes} />
        )}
        {campo('documentos') && <Documentos secao={campo('documentos')} />}
      </S.Pagina>
    </OdsContexto.Provider>
  );
};
